import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiAccountPlusOutline,
	mdiArrowUpBoldCircleOutline,
	mdiArrowDownBoldCircleOutline,
	mdiClose,
	mdiPlusCircleOutline,
	mdiCheckCircle,
	mdiPauseCircleOutline,
	mdiPlusCircle,
} from "@mdi/js";
import {
	Button,
	CircularProgress,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	IconButton,
	LinearProgress,
	TextField,
	Tooltip,
} from "@mui/material";
import { clsx } from "@ismael1361/utils";
import {
	WorkspaceServices,
	PublicTeamMemberDTO,
	PublicTeamDTO,
	McpLibraryServices,
	PublicContractedMcpDTO,
	PublicMcpDTO,
	SeniorityLevel,
	NotistackServices,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

const SENIORITY_LABEL: Record<SeniorityLevel, string> = {
	junior: "Júnior",
	pleno: "Pleno",
	senior: "Sênior",
};
const SENIORITY_ORDER: SeniorityLevel[] = ["junior", "pleno", "senior"];
function nextSeniority(s: SeniorityLevel, delta: 1 | -1): SeniorityLevel | null {
	const idx = SENIORITY_ORDER.indexOf(s);
	const next = idx + delta;
	if (next < 0 || next >= SENIORITY_ORDER.length) return null;
	return SENIORITY_ORDER[next];
}

interface ContractedTabProps {
	workspaceId: string | null;
}

interface TeamWithMembers {
	team: PublicTeamDTO;
	members: PublicTeamMemberDTO[];
}

export const ContractedTab: React.FC<ContractedTabProps> = ({ workspaceId }) => {
	const [loading, setLoading] = useState(true);
	const [teamData, setTeamData] = useState<TeamWithMembers[]>([]);
	const [contractedMcps, setContractedMcps] = useState<PublicContractedMcpDTO[]>([]);
	const [addMcpFor, setAddMcpFor] = useState<{ teamId: string; member: PublicTeamMemberDTO } | null>(null);
	const [savingMemberId, setSavingMemberId] = useState<string | null>(null);
	const [addTokensFor, setAddTokensFor] = useState<{ teamId: string; member: PublicTeamMemberDTO } | null>(null);
	const [tokensInput, setTokensInput] = useState<string>("");
	const [tokensReason, setTokensReason] = useState<string>("");
	const [savingTokens, setSavingTokens] = useState<boolean>(false);

	const openAddTokens = (teamId: string, member: PublicTeamMemberDTO) => {
		setAddTokensFor({ teamId, member });
		setTokensInput("");
		setTokensReason("");
	};

	const handleAddTokens = async () => {
		if (!addTokensFor || !workspaceId) return;
		const n = Number(tokensInput);
		if (!Number.isInteger(n) || n <= 0) {
			NotistackServices.showErrorMessage("Informe um número inteiro positivo de tokens.");
			return;
		}
		const targetTeamId = String(addTokensFor.teamId);
		const targetMemberId = addTokensFor.member.id;
		setSavingTokens(true);
		try {
			const updated = await WorkspaceServices.addMemberTokens(
				workspaceId,
				targetTeamId,
				targetMemberId,
				n,
				tokensReason.trim() || undefined,
			);
			// Atualizacao otimista com merge defensivo: alguns campos do DTO retornado
			// podem vir como null/undefined dependendo do estado do membro; preservamos
			// o que ja tinhamos no state quando o updated nao trouxer valor explicito.
			setTeamData((prev) =>
				prev.map((t) => {
					if (String(t.team.id) !== targetTeamId) return t;
					return {
						...t,
						members: t.members.map((m) => {
							if (m.id !== targetMemberId) return m;
							return {
								...m,
								...updated,
								tokensUsed: updated.tokensUsed ?? m.tokensUsed,
								tokensBonus: updated.tokensBonus ?? m.tokensBonus,
								tokenLimit: updated.tokenLimit ?? m.tokenLimit,
								tokenLimitReached:
									(updated.tokensUsed ?? m.tokensUsed) >=
									(updated.tokenLimit ?? m.tokenLimit ?? 0) + (updated.tokensBonus ?? m.tokensBonus ?? 0),
							};
						}),
					};
				}),
			);
			NotistackServices.showSuccessMessage(`+${n.toLocaleString("pt-BR")} tokens adicionados a ${addTokensFor.member.name}`);
			setAddTokensFor(null);
			// Refetch silencioso para garantir sincronia caso o updated venha com algum
			// campo incompleto (ex.: backend cache miss + parcial). Roda em background;
			// nao bloqueia o fechamento do modal nem mostra spinner.
			void loadAll({ silent: true });
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao adicionar tokens");
		} finally {
			setSavingTokens(false);
		}
	};

	const loadAll = useCallback(async (opts?: { silent?: boolean }) => {
		if (!workspaceId) return;
		const silent = opts?.silent === true;
		if (!silent) setLoading(true);
		try {
			const [teams, mcpsHired] = await Promise.all([WorkspaceServices.listTeams(workspaceId), McpLibraryServices.listMcpsHired()]);
			setContractedMcps(mcpsHired);
			// Fetch members em paralelo para cada team
			const withMembers = await Promise.all(
				teams.map(async (team) => {
					try {
						const members = await WorkspaceServices.getMembers(workspaceId, String(team.id));
						return { team, members: members.filter((m) => m.isActive) };
					} catch {
						return { team, members: [] as PublicTeamMemberDTO[] };
					}
				}),
			);
			setTeamData(withMembers.filter((t) => t.members.length > 0));
		} catch (error) {
			if (!silent) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar contratados");
			}
		} finally {
			if (!silent) setLoading(false);
		}
	}, [workspaceId]);

	useEffect(() => {
		void loadAll();
	}, [loadAll]);

	const totalMembers = useMemo(() => teamData.reduce((acc, t) => acc + t.members.length, 0), [teamData]);

	// Mapeia mcp_id → contracted_mcp ativo do workspace owner. Útil pra saber quais MCPs
	// estão "disponíveis" pra atribuir aos membros (precisam estar contratados no workspace).
	const availableMcpById = useMemo(() => {
		const map = new Map<string, PublicContractedMcpDTO>();
		for (const c of contractedMcps) {
			if (c.isActive && c.mcp) map.set(String(c.mcpId), c);
		}
		return map;
	}, [contractedMcps]);

	const handlePromote = async (teamId: string, member: PublicTeamMemberDTO, direction: 1 | -1) => {
		if (!workspaceId) return;
		const next = nextSeniority(member.seniorityLevel, direction);
		if (!next) return;
		setSavingMemberId(member.id);
		try {
			const updated = await WorkspaceServices.updateMember(workspaceId, teamId, member.id, { seniority_level: next });
			setTeamData((prev) =>
				prev.map((t) =>
					t.team.id.toString() !== teamId ? t : { ...t, members: t.members.map((m) => (m.id === member.id ? updated : m)) },
				),
			);
			NotistackServices.showSuccessMessage(
				direction > 0 ? `${member.name} promovido(a) para ${SENIORITY_LABEL[next]}` : `${member.name} rebaixado(a) para ${SENIORITY_LABEL[next]}`,
			);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Falha ao alterar senioridade");
		} finally {
			setSavingMemberId(null);
		}
	};

	const handleRemoveMcp = async (teamId: string, member: PublicTeamMemberDTO, mcpId: string) => {
		if (!workspaceId) return;
		// Sync com is_active=false nesse mcp_id (mantém os outros como estavam).
		const settings = (member.mcps ?? []).map((m) => ({
			mcp_id: String(m.mcpId),
			is_active: String(m.mcpId) === mcpId ? false : m.isActive,
		}));
		setSavingMemberId(member.id);
		try {
			const updated = await WorkspaceServices.updateMember(workspaceId, teamId, member.id, { mcp_settings: settings });
			setTeamData((prev) =>
				prev.map((t) =>
					t.team.id.toString() !== teamId ? t : { ...t, members: t.members.map((m) => (m.id === member.id ? updated : m)) },
				),
			);
			NotistackServices.showSuccessMessage("Ferramenta removida do especialista.");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Falha ao remover ferramenta");
		} finally {
			setSavingMemberId(null);
		}
	};

	const handleAddMcp = async (teamId: string, member: PublicTeamMemberDTO, mcpId: string) => {
		if (!workspaceId) return;
		// Sync: marca o mcp existente como ativo OU inclui novo. Mantém os já ativos.
		const existing = member.mcps ?? [];
		const has = existing.some((m) => String(m.mcpId) === mcpId);
		const settings = has
			? existing.map((m) => ({ mcp_id: String(m.mcpId), is_active: String(m.mcpId) === mcpId ? true : m.isActive }))
			: [...existing.map((m) => ({ mcp_id: String(m.mcpId), is_active: m.isActive })), { mcp_id: mcpId, is_active: true }];
		setSavingMemberId(member.id);
		try {
			const updated = await WorkspaceServices.updateMember(workspaceId, teamId, member.id, { mcp_settings: settings });
			setTeamData((prev) =>
				prev.map((t) =>
					t.team.id.toString() !== teamId ? t : { ...t, members: t.members.map((m) => (m.id === member.id ? updated : m)) },
				),
			);
			NotistackServices.showSuccessMessage("Ferramenta vinculada ao especialista.");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Falha ao vincular ferramenta");
		} finally {
			setSavingMemberId(null);
			setAddMcpFor(null);
		}
	};

	const renderTokenBar = (teamId: string, member: PublicTeamMemberDTO) => {
		const limit = (member.tokenLimit ?? 0) + (member.tokensBonus ?? 0);
		const used = member.tokensUsed ?? 0;
		const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
		const exhausted = limit > 0 && used >= limit;
		return (
			<div className={styles.tokenSection}>
				<div className={styles.tokenLabelRow}>
					<span>Tokens</span>
					<span className={clsx(styles.tokenStats, exhausted && styles.tokenStatsExhausted)}>
						{used.toLocaleString("pt-BR")} / {limit > 0 ? limit.toLocaleString("pt-BR") : "—"}
						{member.tokensBonus > 0 && (
							<span className={styles.tokenBonus}>
								{" "}
								(+ {member.tokensBonus.toLocaleString("pt-BR")} bônus)
							</span>
						)}
					</span>
					<Tooltip title="Adicionar tokens de bônus">
						<IconButton
							size="small"
							onClick={() => openAddTokens(teamId, member)}
							className={clsx(styles.addTokensBtn, exhausted && styles.addTokensBtnExhausted)}
							aria-label="Adicionar tokens"
						>
							<Icon path={mdiPlusCircle} size={0.8} />
						</IconButton>
					</Tooltip>
				</div>
				<LinearProgress
					variant="determinate"
					value={pct}
					className={clsx(styles.tokenBar, exhausted && styles.tokenBarExhausted)}
				/>
			</div>
		);
	};

	if (!workspaceId) return null;

	if (loading) {
		return (
			<div className={styles.loadingContainer}>
				<CircularProgress size={32} />
			</div>
		);
	}

	if (totalMembers === 0) {
		return (
			<div className={styles.emptyContainer}>
				<Icon path={mdiAccountPlusOutline} size={2} />
				<p>Nenhum especialista contratado neste workspace ainda.</p>
				<p className={styles.emptyHint}>Volte na aba <strong>Catálogo</strong> e peça ao Assistente para contratar um.</p>
			</div>
		);
	}

	return (
		<>
			<div className={styles.contractedContainer}>
				{teamData.map(({ team, members }) => (
					<section key={team.id} className={styles.teamSection}>
						<header className={styles.teamHeader}>
							<h3>{team.name}</h3>
							<span className={styles.teamMembersCount}>{members.length} especialista(s)</span>
						</header>
						<div className={styles.contractedList}>
							{members.map((member) => {
								const saving = savingMemberId === member.id;
								const promoteTarget = nextSeniority(member.seniorityLevel, 1);
								const demoteTarget = nextSeniority(member.seniorityLevel, -1);
								return (
									<div key={member.id} className={clsx(styles.contractedCard, saving && styles.contractedCardSaving)}>
										<div className={styles.contractedCardLeft}>
											<div className={styles.cardAvatar}>
												<span>{getInitials(member.name)}</span>
											</div>
										</div>
										<div className={styles.contractedCardBody}>
											<div className={styles.contractedCardHeaderRow}>
												<div className={styles.contractedCardTitleWrap}>
													<h4>{member.name}</h4>
													<Chip
														size="small"
														label={SENIORITY_LABEL[member.seniorityLevel]}
														className={clsx(styles.seniorityChip, styles[`seniority-${member.seniorityLevel}`])}
													/>
													{member.isPaused && (
														<Chip
															size="small"
															icon={<Icon path={mdiPauseCircleOutline} size={0.7} />}
															label="Pausado"
															className={styles.pausedChip}
														/>
													)}
												</div>
												<div className={styles.contractedCardActions}>
													<Tooltip title={demoteTarget ? `Rebaixar para ${SENIORITY_LABEL[demoteTarget]}` : "Já é Júnior"}>
														<span>
															<IconButton
																size="small"
																onClick={() => handlePromote(String(team.id), member, -1)}
																disabled={!demoteTarget || saving}
															>
																<Icon path={mdiArrowDownBoldCircleOutline} size={0.9} />
															</IconButton>
														</span>
													</Tooltip>
													<Tooltip title={promoteTarget ? `Promover para ${SENIORITY_LABEL[promoteTarget]}` : "Já é Sênior"}>
														<span>
															<IconButton
																size="small"
																onClick={() => handlePromote(String(team.id), member, 1)}
																disabled={!promoteTarget || saving}
															>
																<Icon path={mdiArrowUpBoldCircleOutline} size={0.9} />
															</IconButton>
														</span>
													</Tooltip>
												</div>
											</div>

											{member.memberLibrary?.description && (
												<p className={styles.contractedCardDesc}>{member.memberLibrary.description}</p>
											)}

											{renderTokenBar(String(team.id), member)}

											<div className={styles.mcpRow}>
												<span className={styles.mcpRowLabel}>Ferramentas:</span>
												<div className={styles.mcpBadges}>
													{(member.activeMcps ?? []).length === 0 ? (
														<span className={styles.noMcpBadge}>Sem ferramentas ativas</span>
													) : (
														(member.activeMcps ?? []).map((mcpLink) => {
															const mcp = mcpLink.mcp;
															return (
																<Chip
																	key={mcpLink.id}
																	size="small"
																	label={mcp?.name ?? "MCP"}
																	className={styles.mcpBadge}
																	avatar={mcp?.iconUrl ? <img src={mcp.iconUrl} alt={mcp.name} className={styles.mcpBadgeIcon} /> : undefined}
																	onDelete={() => handleRemoveMcp(String(team.id), member, String(mcpLink.mcpId))}
																	deleteIcon={<Icon path={mdiClose} size={0.65} />}
																/>
															);
														})
													)}
													<Button
														size="small"
														startIcon={<Icon path={mdiPlusCircleOutline} size={0.7} />}
														onClick={() => setAddMcpFor({ teamId: String(team.id), member })}
														className={styles.addMcpBtn}
														disabled={saving}
													>
														Adicionar
													</Button>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</section>
				))}
			</div>

			<AddMcpDialog
				open={!!addMcpFor}
				onClose={() => setAddMcpFor(null)}
				member={addMcpFor?.member ?? null}
				teamId={addMcpFor?.teamId ?? ""}
				availableMcpById={availableMcpById}
				onAdd={(mcpId) => addMcpFor && handleAddMcp(addMcpFor.teamId, addMcpFor.member, mcpId)}
			/>

			<Dialog
				open={!!addTokensFor}
				onClose={() => !savingTokens && setAddTokensFor(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>
					Adicionar tokens — {addTokensFor?.member?.name}
				</DialogTitle>
				<DialogContent>
					{addTokensFor && (
						<div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
							<div style={{ fontSize: "0.85rem", color: "var(--mui-palette-text-secondary)" }}>
								Atualmente: <strong>{(addTokensFor.member.tokensUsed ?? 0).toLocaleString("pt-BR")}</strong> usados
								{" "}/ <strong>{((addTokensFor.member.tokenLimit ?? 0) + (addTokensFor.member.tokensBonus ?? 0)).toLocaleString("pt-BR")}</strong> limite
								{addTokensFor.member.tokensBonus > 0 && ` (já com +${addTokensFor.member.tokensBonus.toLocaleString("pt-BR")} bônus)`}.
							</div>
							<TextField
								autoFocus
								type="number"
								label="Tokens a adicionar"
								value={tokensInput}
								onChange={(e) => setTokensInput(e.target.value)}
								inputProps={{ min: 1, step: 1000 }}
								fullWidth
								placeholder="Ex.: 50000"
								helperText="Inteiro positivo. Será somado ao bônus existente."
							/>
							<TextField
								label="Motivo (opcional)"
								value={tokensReason}
								onChange={(e) => setTokensReason(e.target.value)}
								fullWidth
								multiline
								minRows={2}
								maxRows={4}
								placeholder="Ex.: pedido do cliente, ajuste pontual, teste de carga"
								helperText="Registrado no histórico para auditoria."
							/>
						</div>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddTokensFor(null)} disabled={savingTokens}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleAddTokens}
						disabled={savingTokens || !tokensInput || Number(tokensInput) <= 0}
						startIcon={savingTokens ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{savingTokens ? "Adicionando…" : "Adicionar tokens"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

// ─────────────────────────────────────────────────────────────────────
// AddMcpDialog
// ─────────────────────────────────────────────────────────────────────

interface AddMcpDialogProps {
	open: boolean;
	onClose: () => void;
	member: PublicTeamMemberDTO | null;
	teamId: string;
	availableMcpById: Map<string, PublicContractedMcpDTO>;
	onAdd: (mcpId: string) => void;
}

const AddMcpDialog: React.FC<AddMcpDialogProps> = ({ open, onClose, member, availableMcpById, onAdd }) => {
	const activeIds = useMemo(() => new Set((member?.activeMcps ?? []).map((m) => String(m.mcpId))), [member]);

	// Backend só aceita MCPs que estejam vinculados ao template (member_library_mcps).
	// Se libraryMcpIds vier do server, filtramos pra esses; senão (compat com response
	// antiga ou membros legados sem template) mostramos todos.
	const allowedIds = useMemo(() => {
		const ids = member?.libraryMcpIds;
		if (!Array.isArray(ids) || ids.length === 0) return null;
		return new Set(ids.map(String));
	}, [member]);

	const candidates: { mcp: PublicMcpDTO; alreadyActive: boolean }[] = useMemo(() => {
		const list: { mcp: PublicMcpDTO; alreadyActive: boolean }[] = [];
		for (const c of availableMcpById.values()) {
			if (!c.mcp) continue;
			if (allowedIds && !allowedIds.has(String(c.mcpId))) continue;
			list.push({ mcp: c.mcp, alreadyActive: activeIds.has(String(c.mcpId)) });
		}
		return list.sort((a, b) => a.mcp.name.localeCompare(b.mcp.name));
	}, [availableMcpById, activeIds, allowedIds]);

	const usable = candidates.filter((c) => !c.alreadyActive);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ className: styles.dialogPaper }}>
			<DialogTitle className={styles.dialogTitle}>
				<div className={styles.dialogTitleText}>
					<h3>Adicionar ferramenta</h3>
					<span className={styles.dialogTitleSub}>{member?.name}</span>
				</div>
				<IconButton onClick={onClose} size="small" className={styles.dialogClose}>
					<Icon path={mdiClose} size={0.9} />
				</IconButton>
			</DialogTitle>
			<DialogContent className={styles.dialogContent}>
				{candidates.length === 0 ? (
					<p className={styles.dialogHint}>
						{allowedIds
							? `Este especialista (template "${member?.memberLibrary?.name ?? "—"}") só permite ferramentas específicas, e você ainda não contratou nenhuma delas no workspace. Vá em Ferramentas no menu para conectar uma conta primeiro.`
							: "Você ainda não contratou nenhuma ferramenta no workspace. Vá em Ferramentas no menu para conectar uma conta primeiro."}
					</p>
				) : usable.length === 0 ? (
					<p className={styles.dialogHint}>
						Este especialista já está vinculado a todas as ferramentas disponíveis para o template dele.
					</p>
				) : (
					<div className={styles.mcpPickList}>
						{usable.map(({ mcp }) => (
							<button key={mcp.id} className={styles.mcpPickItem} onClick={() => onAdd(String(mcp.id))} type="button">
								{mcp.iconUrl && <img src={mcp.iconUrl} alt={mcp.name} className={styles.mcpPickIcon} />}
								<div className={styles.mcpPickInfo}>
									<span className={styles.mcpPickName}>{mcp.name}</span>
									{mcp.description && <span className={styles.mcpPickDesc}>{mcp.description}</span>}
								</div>
								<Icon path={mdiCheckCircle} size={0.8} className={styles.mcpPickIconRight} />
							</button>
						))}
					</div>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
};
